import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { authService } from '../services/api';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { translateUserApiErrorMessage } from '../utils/translateUserApiError';
import i18n from '../utils/i18n';
import './LanguageSelector.css';
import './NicknameSetup.css';

const APP_LANGUAGES = [
  { code: 'en' as const, name: 'English', flag: 'us' },
  { code: 'pt' as const, name: 'Português', flag: 'br' },
];

const NicknameSetup: React.FC = () => {
  const { t } = useSafeTranslation();
  const [nickname, setNickname] = useState('');
  /** Mandatory before continue — user must tap a flag. */
  const [appLanguage, setAppLanguage] = useState<'en' | 'pt' | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Set when submit fails so empty required fields get a red border */
  const [fieldHighlight, setFieldHighlight] = useState<{
    language: boolean;
    nickname: boolean;
  }>({ language: false, nickname: false });
  const [searchParams] = useSearchParams();

  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      localStorage.setItem('authToken', token);
    }
  }, [token]);

  const validateNickname = (nick: string): string | null => {
    if (!nick.trim()) {
      return t('user.nicknameCannotBeEmpty', 'Nickname cannot be empty.');
    }
    return null;
  };

  const handleNicknameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNickname(value);
    setError(null);
    setFieldHighlight((f) => ({ ...f, nickname: false }));
  };

  /** Switch UI language immediately; preference is saved again on successful submit. */
  const selectAppLanguage = async (code: 'en' | 'pt') => {
    setAppLanguage(code);
    setError(null);
    setFieldHighlight((f) => ({ ...f, language: false }));
    try {
      await i18n.changeLanguage(code);
      localStorage.setItem('quizter-language', code);
    } catch {
      /* ignore i18n errors */
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const langOk = appLanguage !== null;
    const validationError = validateNickname(nickname);
    const nickOk = validationError === null;

    if (!langOk || !nickOk) {
      setFieldHighlight({ language: !langOk, nickname: !nickOk });
      if (!langOk && !nickOk) {
        setError(
          t('nicknameSetup.fillAllRequired', 'Select a language and enter a nickname to continue.')
        );
      } else if (!langOk) {
        setError(t('nicknameSetup.languageRequired', 'Choose your app language before continuing.'));
      } else {
        setError(validationError!);
      }
      return;
    }

    setFieldHighlight({ language: false, nickname: false });
    setIsLoading(true);
    setError(null);

    try {
      await i18n.changeLanguage(appLanguage);
      localStorage.setItem('quizter-language', appLanguage);
      await authService.updateLanguagePreference(appLanguage);

      const user = await authService.updateNickname(nickname);
      localStorage.setItem('user', JSON.stringify(user));
      window.location.href = '/';
    } catch (err: any) {
      if (err.response?.status === 409) {
        setFieldHighlight((f) => ({ ...f, nickname: true }));
        setError(
          t('user.nicknameAlreadyTaken', 'This nickname is already taken. Please choose a different one.')
        );
      } else {
        setFieldHighlight({ language: false, nickname: false });
        const raw = err.response?.data?.message as string | undefined;
        const localized = translateUserApiErrorMessage(raw, t);
        setError(
          localized ??
            t('user.errorUpdatingProfile', 'Error updating profile. Please try again.')
        );
      }
      setIsLoading(false);
    }
  };

  return (
    <div className="nickname-setup-page">
      <Container fluid className="h-100">
        <Row className="h-100 align-items-center justify-content-center">
          <Col xs={12} sm={8} md={6} lg={4} xl={3}>
            <Card className="nickname-setup-card shadow">
              <Card.Body className="text-center p-4">
                <div className="mb-4">
                  <h2 className="setup-title">
                    {t('nicknameSetup.welcomeTitle', 'Welcome to Quizter Club!')}
                  </h2>
                  <p className="text-muted">
                    {t('nicknameSetup.subtitle', 'Choose your nickname to get started')}
                  </p>
                </div>

                <Alert variant="info" className="mb-4">
                  <Alert.Heading>
                    {t('nicknameSetup.aboutHeading', 'About Your Nickname')}
                  </Alert.Heading>
                  <p className="mb-0">
                    {t(
                      'nicknameSetup.aboutBody',
                      'Your nickname will be visible to all players in the platform. Choose something unique and memorable!'
                    )}
                  </p>
                </Alert>

                {error && (
                  <Alert variant="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-4 text-start">
                    <Form.Label className="fw-semibold">
                      {t('nicknameSetup.languageLabel', 'App language')}
                    </Form.Label>
                    <p className="text-muted small mb-2">
                      {t(
                        'nicknameSetup.languageHint',
                        'Choose the language for the Quizter Club interface. You can change this later in settings.'
                      )}
                    </p>
                    <div className="d-flex justify-content-center">
                      <div
                        className={`d-inline-flex align-items-center justify-content-center gap-3 flex-wrap${
                          fieldHighlight.language ? ' nickname-setup-lang-field--error' : ''
                        }`}
                      >
                        {APP_LANGUAGES.map((lang) => (
                          <button
                            key={lang.code}
                            type="button"
                            className={`language-flag-btn language-flag-tile nickname-setup-lang-btn ${
                              appLanguage === lang.code ? 'active' : ''
                            }`}
                            onClick={() => {
                              void selectAppLanguage(lang.code);
                            }}
                            disabled={isLoading}
                            aria-pressed={appLanguage === lang.code}
                            title={lang.name}
                          >
                            <span className={`fi fi-${lang.flag}`} aria-hidden />
                            <span className="visually-hidden">{lang.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    {appLanguage === null && (
                      <Form.Text
                        className={`d-block mt-2 text-center ${
                          fieldHighlight.language ? 'text-danger' : 'text-warning'
                        }`}
                      >
                        {t('nicknameSetup.languageRequiredShort', 'Select a language to continue.')}
                      </Form.Text>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>{t('user.nickname', 'Nickname')}</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={t('user.enterYourNickname', 'Enter your nickname')}
                      value={nickname}
                      onChange={handleNicknameChange}
                      disabled={isLoading}
                      maxLength={30}
                      className={fieldHighlight.nickname ? 'is-invalid' : ''}
                    />
                    <Form.Text className="text-muted">
                      <ul className="text-start mt-2 mb-0">
                        <li>{t('nicknameSetup.ruleLength', '3-30 characters long')}</li>
                        <li>
                          {t(
                            'nicknameSetup.ruleChars',
                            'Letters, numbers, underscores (_) and hyphens (-) only'
                          )}
                        </li>
                        <li>{t('nicknameSetup.ruleUnique', 'Must be unique across the platform')}</li>
                        <li>{t('nicknameSetup.ruleReserved', 'Cannot contain reserved words')}</li>
                      </ul>
                    </Form.Text>
                  </Form.Group>

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="w-100"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {t('nicknameSetup.settingUp', 'Setting up...')}
                      </>
                    ) : (
                      t('nicknameSetup.continueButton', 'Continue to Quizter Club')
                    )}
                  </Button>
                </Form>

                <div className="mt-4">
                  <small className="text-muted">
                    {t(
                      'nicknameSetup.footerHint',
                      'You can change your nickname later in your profile settings.'
                    )}
                  </small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default NicknameSetup;
